import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function DownloadApp() {
  const features = [
    {
      icon: 'ri-map-pin-line',
      title: 'Real-time order tracking',
      description: 'Track your order from kitchen to doorstep',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Secure online payments',
      description: 'Multiple payment options with top-tier security',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Easy reordering',
      description: 'Reorder your favorites with just one tap',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'ri-gift-line',
      title: 'Exclusive promotions',
      description: 'Get access to app-only deals and discounts',
      gradient: 'from-orange-500 to-red-500'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <SEO 
        title="Download Ami Bites App - Food Delivery App for Android & iOS | Zimbabwe"
        description="Download the Ami Bites mobile app for easy food ordering. Real-time tracking, secure payments, exclusive deals. Coming soon on Google Play & App Store."
        keywords="Ami Bites app download, food delivery app Zimbabwe, mobile food ordering, Android food app, iOS food app, restaurant app Zimbabwe, food delivery mobile app"
        canonical="https://amibites.com/download-app"
      />
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-orange-500 to-pink-600 rounded-3xl mb-6 shadow-2xl"
            >
              <i className="ri-smartphone-line text-5xl text-white"></i>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get the Ami Bites App
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Order food anytime, anywhere with the Ami Bites mobile application.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center shadow-lg`}
                    >
                      <i className={`${feature.icon} text-3xl text-white`}></i>
                    </motion.div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Download now:
            </h2>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <motion.a
                href="https://play.google.com/store/apps/details?id=amibites.com.myapp&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="relative flex items-center space-x-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white px-8 py-5 rounded-2xl shadow-xl hover:shadow-2xl transition-all w-72 overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl">
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                </div>
                <div className="text-left relative z-10">
                  <div className="text-xs opacity-80">GET IT ON</div>
                  <div className="text-lg font-bold">Google Play</div>
                </div>
              </motion.a>

              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="relative flex items-center space-x-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white px-8 py-5 rounded-2xl shadow-xl opacity-60 cursor-not-allowed transition-all w-72 overflow-hidden group"
                disabled
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 transition-opacity" />
                <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl">
                  <i className="ri-apple-fill text-3xl"></i>
                </div>
                <div className="text-left relative z-10">
                  <div className="text-xs opacity-80">Download on the</div>
                  <div className="text-lg font-bold">App Store</div>
                </div>
              </motion.button>
            </div>

            <p className="mt-8 text-gray-500 text-sm">
              iOS version coming soon! Stay tuned for updates.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
